import type { LearnerSurvey } from '~/gql/surveys/surveys'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { GQL_SKIP_SURVEY, GQL_SUBMIT_SURVEY } from '~/gql/surveys/surveys'
import RatingField from './RatingField'

type IProps = {
  sessionId: string
  survey: LearnerSurvey
  /** Questionnaire rempli ou passé : l'attestation part. */
  onDone: () => void
}

/**
 * Le questionnaire de fin de parcours (#320), proposé avant l'attestation et
 * toujours facultatif : « Passer » mène à l'attestation comme « Envoyer ». Les
 * notes de modules sont l'évaluation des contenus (V10, indicateur 33).
 */
function SurveyDialog({ sessionId, survey, onDone }: IProps) {
  const { t } = useTranslation()
  const [ratings, setRatings] = useState<Record<string, number>>({})
  const [texts, setTexts] = useState<Record<string, string>>({})
  const [moduleRatings, setModuleRatings] = useState<Record<string, number>>({})
  const [submit, { loading: submitting }] = useMutation(GQL_SUBMIT_SURVEY)
  const [skip, { loading: skipping }] = useMutation(GQL_SKIP_SURVEY)

  // Une réponse perdue ne retient pas l'attestation : on enchaîne quoi qu'il arrive.
  const finish = (action: () => Promise<unknown>) => () => void action().catch(() => {}).finally(onDone)

  const send = finish(() => submit({
    variables: {
      input: {
        sessionId,
        answers: survey.questions.map(q => (q.kind === 'SCALE' ? { questionId: q.id, rating: ratings[q.id] ?? null } : { questionId: q.id, text: texts[q.id] ?? null })),
        moduleRatings: Object.entries(moduleRatings).map(([moduleId, rating]) => ({ moduleId, rating })),
      },
    },
  }))

  return (
    <Dialog open onOpenChange={open => !open && finish(() => skip({ variables: { sessionId } }))()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('survey.title')}</DialogTitle>
          <DialogDescription>{t('survey.intro')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {survey.questions.map(q => (q.kind === 'SCALE'
            ? <RatingField key={q.id} legend={q.label} value={ratings[q.id] ?? null} onChange={value => setRatings(r => ({ ...r, [q.id]: value }))} />
            : (
                <div key={q.id} className="flex flex-col gap-1.5">
                  <label htmlFor={`survey-${q.id}`} className="text-sm font-medium">{q.label}</label>
                  <textarea id={`survey-${q.id}`} rows={3} maxLength={2000} value={texts[q.id] ?? ''} onChange={e => setTexts(x => ({ ...x, [q.id]: e.target.value }))} className="rounded-md border border-input bg-transparent px-3 py-2 text-sm" />
                </div>
              )))}
          {survey.modules.length > 0 && (
            <div className="flex flex-col gap-4 border-t border-border pt-4">
              <p className="text-sm font-medium">{t('survey.modules.title')}</p>
              {survey.modules.map(m => (
                <RatingField key={m.id} legend={m.title} value={moduleRatings[m.id] ?? null} onChange={value => setModuleRatings(r => ({ ...r, [m.id]: value }))} />
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={skipping || submitting} onClick={finish(() => skip({ variables: { sessionId } }))}>{t('survey.skip')}</Button>
          <Button disabled={skipping || submitting} onClick={send}>{t('survey.submit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SurveyDialog
